import React, { useEffect, useId, useRef, useState } from "react";

export function AdminConfirmModal({
  open,
  onClose,
  title,
  message,
  confirmLabel = "Delete",
  cancelLabel = "Cancel",
  onConfirm,
  danger = true,
}) {
  const titleId = useId();
  const cancelRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const id = requestAnimationFrame(() => cancelRef.current?.focus());
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      cancelAnimationFrame(id);
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="admin-dash__modal-root"
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
    >
      <button
        type="button"
        className="admin-dash__modal-backdrop"
        aria-label="Close dialog"
        onClick={onClose}
      />
      <div className="admin-dash__modal-panel">
        <h2 id={titleId} className="admin-dash__modal-title">
          {title}
        </h2>
        <p className="admin-dash__modal-body">{message}</p>
        <div className="admin-dash__modal-actions">
          <button ref={cancelRef} type="button" className="admin-dash__btn-secondary" onClick={onClose}>
            {cancelLabel}
          </button>
          <button
            type="button"
            className={danger ? "admin-dash__btn-danger" : "admin-dash__btn-primary"}
            onClick={() => {
              onConfirm();
              onClose();
            }}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

export function AdminTypedDeleteModal({
  open,
  onClose,
  onConfirm,
  title,
  description,
  phrase = "Delete",
  confirmButtonLabel,
}) {
  const [input, setInput] = useState("");
  const titleId = useId();
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open) {
      setInput("");
      return;
    }
    const id = requestAnimationFrame(() => inputRef.current?.focus());
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      cancelAnimationFrame(id);
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  const match = input.trim() === phrase;
  const label = confirmButtonLabel || `${phrase} permanently`;

  if (!open) return null;

  return (
    <div
      className="admin-dash__modal-root"
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
    >
      <button
        type="button"
        className="admin-dash__modal-backdrop"
        aria-label="Close dialog"
        onClick={onClose}
      />
      <div className="admin-dash__modal-panel admin-dash__modal-panel--typed">
        <h2 id={titleId} className="admin-dash__modal-title admin-dash__modal-title--danger">
          {title}
        </h2>
        <p className="admin-dash__modal-body">{description}</p>
        <label className="admin-dash__label admin-dash__modal-typed-label">
          Type <strong className="admin-dash__modal-phrase">{phrase}</strong> to confirm
          <input
            ref={inputRef}
            className="admin-dash__input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            autoComplete="off"
            spellCheck={false}
            aria-invalid={input.length > 0 && !match}
          />
        </label>
        <div className="admin-dash__modal-actions">
          <button type="button" className="admin-dash__btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="admin-dash__btn-danger"
            disabled={!match}
            onClick={() => {
              if (!match) return;
              onConfirm();
              onClose();
            }}
          >
            {label}
          </button>
        </div>
      </div>
    </div>
  );
}
